<template>
  <div>
    <!-- Automation List -->
    <automation-list
      v-show="!isEditing"
      :allEntities="allEntities"
      :loadComponent="loadComponent"
      :loadText="loadingText"
      :refreshKey="refreshKey"
      @add-automation="handleAddNew"
      @edit-automation="handleAutomationSelection"
    />

    <!-- Automation Edit -->
    <automation-edit
      v-if="isEditing && selectedEntity"
      :currentEntity="selectedEntity"
      :entity_id="selectedEntity._id"
      :allEntities="allEntities"
      :allEntityFilters="allEntityFilters"
      :companyUsers="companyUsers"
      :allContacts="allContacts"
      :fromEntity="fromEntity"
      :automationData="currentAutomation"
      :isNew="isNewAutomation"
      :allTemplates="allTemplates"
      :templateFieldMap="templateFieldMap"
      :allTemplateFieldMap="allTemplateFieldMap"
      :companyAllTemplateFields="companyAllTemplateFields"
      :allFormbuilders="allFormbuilders"
      :entityAllFilters="entityAllFilters"
      @saved="handleSaveSuccess"
      @cancel="handleCancelEdit"
    />

    <!-- 🔹 Entity Selection Dialog with Cards -->
    <dialog-component
      title="Select Entity"
      :visible="showEntityDialog"
      :containerWidth="getIsMobile ? '90%' : '70%'"
      @close="showEntityDialog = false"
      @before-close="showEntityDialog = false"
    >
      <div class="p-2 max-h-[70vh] overflow-y-auto">
        <!-- Search Bar -->
        <div class="mb-2">
          <el-input
            v-model="searchQuery"
            placeholder="Search entities..."
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
          />
        </div>

        <!-- Cards Grid -->
        <div
          class="grid gap-2 mb-5"
          :class="{
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4': true,
          }"
        >
          <div
            v-for="entity in filteredEntities"
            :key="entity._id"
            class="border-2 rounded-lg p-4 cursor-pointer bg-white flex flex-col justify-between transition-all duration-300 ease-in-out hover:border-blue-500 hover:shadow-md hover:-translate-y-0.5 active:scale-95"
            :class="{
              'border-blue-500 bg-blue-50': selectedEntityId === entity._id,
              'border-gray-200': selectedEntityId !== entity._id,
            }"
            @click="handleAutomationSelection(null, entity._id)"
          >
            <!-- Header -->
            <div class="flex items-start gap-3 mb-3">
              <div class="text-blue-500 text-2xl mt-1">
                <i class="el-icon-folder-opened"></i>
              </div>
              <div>
                <h4 class="text-gray-800 text-base font-semibold mb-1">
                  {{ entity.name }}
                </h4>
                <span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                  {{ entity.entity_type || 'Entity' }}
                </span>
              </div>
            </div>

            <!-- Description -->
            <div class="flex-1 mb-3">
              <p v-if="entity.description" class="text-sm text-gray-600 line-clamp-2">
                {{ truncateDescription(entity.description) }}
              </p>
              <p v-else class="text-sm italic text-gray-400">No description available</p>
            </div>

            <!-- Footer -->
            <div class="flex justify-between items-center">
              <span v-if="entity.created_at" class="text-xs text-gray-500">
                Created: {{ formatDate(entity.createdAt) }}
              </span>
              <el-tag v-if="selectedEntityId === entity._id" type="success" size="small">
                Selected
              </el-tag>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="filteredEntities.length === 0"
          class="text-center py-10 text-gray-400 flex flex-col items-center justify-center"
        >
          <i class="el-icon-search text-5xl mb-4"></i>
          <p class="text-gray-500">No entities found matching "{{ searchQuery }}"</p>
        </div>
      </div>
    </dialog-component>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import AutomationEdit from './AutomationEdit.vue';
  import AutomationList from './AutomationList.vue';
  import { postAPICall } from '../../../helpers/httpHelper';
  import templateBuilderHelper from '../../../mixins/templateBuilderHelper';
  import EntitiesHelper from '../../../mixins/EntitiesHelper';
  import AutomationHelper from '../../../mixins/AutomationHelper';

  export default {
    name: 'AutomationWorkspace',
    components: { AutomationList, AutomationEdit },
    props: ['currentEntity', 'fromEntity'],
    mixins: [templateBuilderHelper, EntitiesHelper, AutomationHelper],
    data() {
      return {
        isEditing: false,
        isNewAutomation: false,
        refreshKey: 0,
        currentAutomation: null,
        allEntities: [],
        dataFetched: false,
        selectedEntity: null,
        selectedEntityId: null,
        showEntityDialog: false,
        loadComponent: false,
        loadingText: 'fetching entity, please wait...',
        searchQuery: '',
        filteredEntities: [],
        allEntityFilters: {},
        allContacts: [],
        companyUsers: [],
        allTemplates: [],
        templateFieldMap: {},
        allTemplateFieldMap: {},
        companyAllTemplateFields: {},
        allFormbuilders: [],
        entityAllFilters: [],
      };
    },
    computed: {
      ...mapGetters('navigationOpen', ['getIsMobile', 'getIsRSOpen']),
      ...mapGetters('auth', ['getAuthenticatedUser']),
    },
    async mounted() {
      await this.fetchRequiredData();
      this.dataFetched = true;
    },
    methods: {
      handleAddNew() {
        if (!this.dataFetched) {
          return this.$message('System is loading necessary resources. Please wait a few seconds.');
        }
        this.showEntityDialog = true;
        this.selectedEntityId = null;
        this.searchQuery = '';
        this.filteredEntities = [...this.allEntities];
      },
      async fetchRequiredData() {
        await this.fetchInitialData();
        await this.fetchInfo();
      },
      handleSearch() {
        if (!this.searchQuery.trim()) {
          this.filteredEntities = [...this.allEntities];
          return;
        }
        const query = this.searchQuery.toLowerCase();
        this.filteredEntities = this.allEntities.filter(
          (entity) =>
            entity.name.toLowerCase().includes(query) ||
            (entity.description && entity.description.toLowerCase().includes(query)) ||
            (entity.type && entity.type.toLowerCase().includes(query))
        );
      },
      truncateDescription(description, maxLength = 100) {
        if (description.length <= maxLength) return description;
        return description.substring(0, maxLength) + '...';
      },
      formatDate(dateString) {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
      async handleAutomationSelection(automation = null, entity_id = null) {
        try {
          if (!this.dataFetched) {
            return this.$message(
              'System is loading necessary resources. Please wait a few seconds.'
            );
          }
          let targetEntityId;
          let isEditMode = !!automation;

          if (isEditMode) {
            targetEntityId = automation.entity_id;
          } else {
            targetEntityId = entity_id;
          }

          this.selectedEntity = this.allEntities.find((e) => e._id == targetEntityId);

          this.loadComponent = false;
          this.isEditing = true;
          this.selectedEntityId = targetEntityId;

          if (isEditMode) {
            this.isNewAutomation = false;
            this.currentAutomation = automation;
          } else {
            this.isNewAutomation = true;
            this.currentAutomation = null;
            this.showEntityDialog = false;
          }
          this.entityAllFilters = this.allEntityFilters[targetEntityId];
          console.log('this.allEntityFilters', this.allEntityFilters, targetEntityId);
        } catch (error) {
          console.error('Error in handleAutomationSelection:', error);
          this.loadComponent = false;
        }
      },

      handleSaveSuccess() {
        this.isEditing = false;
        this.refreshKey += 1;
      },
      handleCancelEdit() {
        this.resetState();
      },
      async fetchInitialData() {
        [this.allEntities, this.allTemplates, this.allFormbuilders] = await Promise.all([
          this.fetchAllEntities(),
          this.fetchAllTemplates(),
          this.fetchAllFormbuilders(),
        ]);
        (this.allTemplates || []).forEach((template) => {
          this.templateFieldMap[template._id] = {
            name: template.name,
            id: template._id,
            fields: this.getFieldsFromTemplate(template, false, false),
          };
          this.allTemplateFieldMap[template._id] = {
            name: template.name,
            id: template._id,
            fields: this.getFieldsFromTemplate(template, true, true),
          };
        });
        if (this.allTemplates?.length) {
          this.companyAllTemplateFields = this.getAllTemplateFieldsObject(this.allTemplates);
        }
        this.allEntities = this.allEntities.map((entity) => {
          if (entity?.templates) {
            entity.templates = entity.templates.map((template) => {
              template.templateInfo = this.allTemplates.find(
                (temp) => temp._id == template.template_id
              );
              return template;
            });
          }
          return entity;
        });
      },
      async fetchInfo() {
        const [filters, contacts, companyUsers] = await Promise.all([
          postAPICall('GET', '/filters', {
            entities: this.allEntities.flatMap((e) => e._id),
          }),
          postAPICall('GET', '/contacts', {
            get_all: true,
            type: 'CONTACT',
          }),
          postAPICall('GET', '/user/companyUsers'),
        ]);
        (filters?.data || []).forEach((el) => {
          if (this.allEntityFilters[el.entity_id]) {
            this.allEntityFilters[el.entity_id].push(el);
          } else {
            this.allEntityFilters[el.entity_id] = [el];
          }
        });
        this.allContacts = contacts?.data || [];
        if (companyUsers) {
          this.companyUsers = companyUsers.data || [];
          if (!this.companyUsers.flatMap((e) => e._id).includes(this.getAuthenticatedUser._id)) {
            this.companyUsers.push(this.getAuthenticatedUser);
          }
        }
      },
      resetState() {
        this.selectedEntity = null;
        this.isEditing = false;
        this.currentAutomation = null;
      },
    },
  };
</script>
